import { Link, useNavigate } from '@tanstack/react-router';

import { useGapReviewData } from '../gap-review-context';
import { dateTime, shortDate } from '../lib/format';
import { DEFAULT_QUEUE_SEARCH } from '../lib/queue';
import { DEFAULT_READER_VIEW } from '../lib/reader-view';
import { totalsOf, verdictTone } from '../lib/run-audit';
import { useGapExecution } from '@/shared/hooks/useGapReview';
import { CandidateFunnel } from './CandidateFunnel';
import { RunAgentsSection } from './RunAgentsSection';
import { RunCoverageSection } from './RunCoverageSection';
import { RunLoopSection } from './RunLoopSection';
import { SalesCoverageSection } from './SalesCoverageSection';

/**
 * One execution's whole record: the funnel behind its gaps, the loop that found
 * them, and what it could not cover.
 *
 * Its own view rather than a rail tab, because none of this is what a reviewer
 * needs mid-queue — it is what they need when a gap looks wrong and the question
 * becomes whether the run that produced it can be trusted at all.
 *
 * The execution is fetched by id, so this page is reachable by link from anywhere
 * without the queue having loaded first. The picker and the back link come off the
 * run summaries, which are counted in the database — the gap count on the back
 * link costs nothing beyond the list the picker was already drawn from.
 */
export function RunAuditView({ executionId }: { executionId: string }) {
  const { drafts, executions } = useGapReviewData();
  const { data: execution, isPending, error } = useGapExecution(executionId);
  const navigate = useNavigate();

  // The summary is the authority on how many gaps a run holds; the loaded drafts
  // stand in only for a run the summaries have not caught up with.
  const draftCount =
    executions.find((summary) => summary.executionId === executionId)
      ?.draftCount ?? drafts.length;

  if (isPending) {
    return (
      <div className="gr-view gr-page">
        <div className="gr-page-in">
          <div className="gr-empty">
            <b>Loading</b>
            Fetching the execution.
          </div>
        </div>
      </div>
    );
  }

  if (error !== null) {
    return (
      <div className="gr-view gr-page">
        <div className="gr-page-in">
          <div className="gr-empty">
            <b>Could not load the execution</b>
            {error.message}
          </div>
          <Link
            className="gr-back"
            to="/gap-review"
            search={{ ...DEFAULT_QUEUE_SEARCH, execution: executionId }}
          >
            ← Back to the review queue
          </Link>
        </div>
      </div>
    );
  }

  const totals = totalsOf(execution);
  const scopeNames = execution.scopes
    .map((scope) => scope.scopeName)
    .join(' · ');

  return (
    <div className="gr-view gr-page">
      <div className="gr-runband">
        <div className="gr-runband-in">
          <div>
            <h2>
              Execution · {shortDate(execution.completedAt)} · {scopeNames}
            </h2>
            <div className="gr-statline">
              <span>
                Completed <b>{dateTime(execution.completedAt)}</b>
              </span>
              <span>
                Data cutoff <b>{shortDate(execution.dataCutoff)}</b>
              </span>
              <span>
                Iterations <b>{execution.runsExecuted}</b>
              </span>
              <span>
                Scopes <b>{totals.scopeCount}</b>
              </span>
              <span>
                Gaps <b>{totals.gapCount}</b>
              </span>
              <span>
                Candidates <b>{totals.candidateCount}</b>
              </span>
              <span>
                Variant <b>{execution.variant}</b>
              </span>
              {/* Absent on executions that predate the field, and nothing
                  backfilled them — so it is omitted rather than shown empty.
                  The per-agent breakdown is the Agents section below. */}
              {!!execution.models?.length && (
                <span>
                  Model <b>{execution.models.join(', ')}</b>
                </span>
              )}
              <span data-tone="amber">Stop reason: {execution.stopReason}</span>
            </div>
          </div>

          <div className="gr-runband-end">
            <div className="gr-select-wrap gr-select-wrap-fixed">
              <select
                aria-label="Execution"
                value={executionId}
                onChange={(changeEvent) =>
                  void navigate({
                    to: '/gap-review/run/$executionId',
                    params: { executionId: changeEvent.target.value },
                  })
                }
              >
                {executions.map((summary) => (
                  <option key={summary.executionId} value={summary.executionId}>
                    {summary.scopes.map((scope) => scope.scopeName).join(', ')}{' '}
                    · {shortDate(summary.completedAt)}
                  </option>
                ))}
              </select>
            </div>
            <div className="gr-verdictpills">
              {totals.verdictCounts.map(({ verdict, count }) => (
                <span
                  className="gr-vpill"
                  key={verdict}
                  data-tone={verdictTone(verdict)}
                >
                  {count} {verdict.toLowerCase()}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="gr-page-in gr-page-in-wide">
        {/* Back to the gaps this execution produced rather than into history:
            the audit is most often opened to decide whether to trust a run, and
            the useful next step is the run's own queue. */}
        <Link
          className="gr-back"
          to="/gap-review"
          search={{
            execution: executionId,
            filter: 'all',
            view: DEFAULT_READER_VIEW,
          }}
        >
          ← Back to this execution&apos;s {draftCount} gaps
        </Link>

        <div className="gr-stack-lg">
          <CandidateFunnel execution={execution} />
          <RunAgentsSection execution={execution} />
          <RunLoopSection execution={execution} />
          <RunCoverageSection execution={execution} />
          <SalesCoverageSection scopes={execution.scopes} />
        </div>
      </div>
    </div>
  );
}
